import React from 'react';
import type { EventCustomField, FieldType } from '../types/database';

interface DynamicFormFieldProps {
  field: EventCustomField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
  isPreFilled?: boolean; // Shows "From profile" badge when true
}

/**
 * Renders a form field dynamically based on the field type from event_custom_fields.
 * Supports: text, textarea, select, radio, checkbox, date, number
 */
const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled = false,
  isPreFilled = false,
}) => {
  const renderField = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text || ''}
            disabled={disabled}
            required={field.is_required}
          />
        );

      case 'textarea':
        return (
          <textarea
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text || ''}
            disabled={disabled}
            required={field.is_required}
            rows={4}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder_text || ''}
            disabled={disabled}
            required={field.is_required}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required={field.is_required}
          />
        );

      case 'select':
        return (
          <select
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required={field.is_required}
          >
            <option value="">{field.placeholder_text || 'Select an option'}</option>
            {field.field_options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="radio-group">
            {field.field_options?.map((option) => (
              <div key={option} className="form-check mb-2">
                <input
                  type="radio"
                  className="form-check-input"
                  id={`${field.field_key}-${option}`}
                  name={field.field_key}
                  value={option}
                  checked={value === option}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabled}
                  required={field.is_required}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${field.field_key}-${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        );

      case 'checkbox':
        // For checkboxes, value is an array of selected options
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="checkbox-group">
            {field.field_options?.map((option) => (
              <div key={option} className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`${field.field_key}-${option}`}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...selectedValues, option]);
                    } else {
                      onChange(selectedValues.filter((v: string) => v !== option));
                    }
                  }}
                  disabled={disabled}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${field.field_key}-${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        );

      default:
        return (
          <input
            type="text"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder_text || ''}
            disabled={disabled}
            required={field.is_required}
          />
        );
    }
  };

  return (
    <div className="form-group mb-3">
      <label className="form-label">
        {field.field_label}
        {field.is_required && <span className="text-danger ms-1">*</span>}
        {isPreFilled && (
          <span
            className="badge bg-success ms-2"
            style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
          >
            From profile
          </span>
        )}
      </label>

      {renderField()}

      {field.help_text && (
        <small className="form-text text-muted">{field.help_text}</small>
      )}

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

/**
 * Utility function to validate a custom field value
 */
export function validateCustomField(
  field: EventCustomField,
  value: any
): { valid: boolean; error?: string } {
  if (!field.is_required) {
    return { valid: true };
  }

  // Check if value is empty
  const isEmpty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  if (isEmpty) {
    return {
      valid: false,
      error: `${field.field_label} is required`,
    };
  }

  return { valid: true };
}

/**
 * Get the default value for a field type
 */
export function getDefaultValue(fieldType: FieldType): any {
  switch (fieldType) {
    case 'checkbox':
      return [];
    case 'number':
      return '';
    default:
      return '';
  }
}

export default DynamicFormField;
